import { useEffect, useState } from "react";
import "../blocks/galleryMigrationBanner/galleryMigrationBanner.css";
import {
  loadGalleryItems,
  clearGalleryItems,
  dataUrlToFile,
} from "../utils/galleryStorage";
import api from "../utils/api";

// One-time prompt: if this browser has old Album data saved locally from
// before the /gallery backend route existed, offer to upload it to the
// person's real account so it syncs across devices like everything else.
const GalleryMigrationBanner = ({ userId, onMigrated }) => {
  const [legacyItems, setLegacyItems] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | migrating | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    if (!userId) return;
    const items = loadGalleryItems(userId);
    setLegacyItems(items);
  }, [userId]);

  if (legacyItems.length === 0 || status === "done") {
    return null;
  }

  const handleMigrate = async () => {
    setStatus("migrating");
    setProgress({ done: 0, total: legacyItems.length });

    let successCount = 0;
    for (const item of legacyItems) {
      try {
        if (item.source === "link") {
          await api.addGalleryLink({
            kind: item.kind,
            url: item.url,
            title: item.title,
          });
        } else if (item.source === "upload") {
          const file = await dataUrlToFile(
            item.url,
            item.title || "upload",
            item.mimeType
          );
          await api.uploadGalleryFile(file, item.title);
        }
        successCount += 1;
      } catch (err) {
        console.error("Failed to migrate a gallery item:", err);
      }
      setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
    }

    if (successCount > 0) {
      clearGalleryItems(userId);
      onMigrated();
    }
    setStatus(successCount === legacyItems.length ? "done" : "error");
  };

  const handleDismiss = () => {
    clearGalleryItems(userId);
    setLegacyItems([]);
  };

  return (
    <div className="galleryMigrationBanner">
      {status === "idle" && (
        <>
          <p className="galleryMigrationBanner__text">
            Found {legacyItems.length} item(s) saved on this device from
            before — move them to your account so they sync everywhere?
          </p>
          <div className="galleryMigrationBanner__actions">
            <button
              type="button"
              className="galleryMigrationBanner__button"
              onClick={handleMigrate}
            >
              Move to my account
            </button>
            <button
              type="button"
              className="galleryMigrationBanner__dismiss"
              onClick={handleDismiss}
            >
              Discard instead
            </button>
          </div>
        </>
      )}
      {status === "migrating" && (
        <p className="galleryMigrationBanner__text">
          Moving {progress.done} of {progress.total}…
        </p>
      )}
      {status === "error" && (
        <p className="galleryMigrationBanner__text">
          Some items couldn't be moved. The rest are safe in your account —
          you can try again later; the rest will stay on this device for now.
        </p>
      )}
    </div>
  );
};

export default GalleryMigrationBanner;
